import { useModal } from "hooks/useModal";
import { useState, useEffect, useMemo } from "react";
import ReactCountryFlag from "react-country-flag";
import { useGetTaskQuery } from "store/apis/task";
import { formatDate } from "utils/formatDate";
import { ChatPane } from "components/ChatPane/ChatPane";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Preview } from "components/PreviewModal";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Tab from "components/DesignSystem/Tab/Tab";
import Slider from "components/DesignSystem/Slider/Slider";
import { Button } from "components/DesignSystem/Button/Button";
import { Field, Form, Formik, useFormikContext } from "formik";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { useGetAllInkleCPATeamsQuery } from "store/apis/internalTeamSetting";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import {
  useGetServiceTeamQuery,
  useUpdateTaskAssigneeAdminMutation,
  useUpdateTaskAssigneeProviderMutation,
} from "store/apis/serviceTeam";
import { FormikListener } from "components/FormikListener/FormikListener";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { FieldProps } from "formik";
import { YYYY_MM_DD } from "constants/date";
import Loader from "components/DesignSystem/Loader/loader";
import { ConditionalLink } from "components/conditionalLink";
import { Link } from "components/DesignSystem/Link/Link";
import { TaskStatus } from "components/TaskStatus/TaskStatus";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ETDUpdateReason } from "./ETDUpdateReason/ETDUpdateReason";
import dayjs from "dayjs";
import { BackendError } from "types/utils/error";
import { useToast } from "hooks/useToast";
import { EtdUpdateHistoryCard } from "./EtdUpdateHistoryCard";
import { ServiceTeamChangeModal } from "./ServiceTeamChangeModal";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { TaskSliderTabs } from "types/Models/task";
import { Tag } from "components/DesignSystem/Tag/Tag";
import ToolTip from "components/design/toolTip";
import { TaskDeadlineInfo } from "./TaskDeadlineInfo";
import { TASK_SLIDER_TABS } from "constants/task";
import { validate } from "uuid";
import { ActionCentre } from "components/ChannelinfoSlider/ChannelInfo/ActionCentre/ActionCentre";
import { UnstyledChannel } from "components/UnstyledChannel/UnstyledChannel";
import { useChatContext } from "stream-chat-react";
import { ChatActionCentreSlider } from "components/ChannelinfoSlider/ChannelInfo/ActionCentre/ChatActionCentreSlider";
import { useAppSelector } from "hooks/useAppSelector";
import { TaskActionDetails } from "./TaskActionDetails";

type FormData = {
  serviceTeamId: string;
  preparerId: string;
};

const ReviewerSelector = ({
  task,
}: {
  task: { uuid: string; reviewer?: { uuid?: string; name?: string } };
}) => {
  const { values } = useFormikContext<FormData>();
  const { isCustomer } = useRoleBasedView();
  const { alertToast, successToast } = useToast();
  const serviceTeamIds = values.serviceTeamId
    ?.split(",")
    .filter((id) => validate(id))
    .join(",");
  const { data: serviceTeam = [], isLoading: serviceTeamLoading } =
    useGetServiceTeamQuery(
      {
        serviceTeamId: serviceTeamIds!,
      },
      { skip: !serviceTeamIds || isCustomer }
    );

  const [updateTaskAssigneeAdmin, { isLoading: isUpdating }] =
    useUpdateTaskAssigneeAdminMutation();

  const selectedReviewer = serviceTeam.find(
    (m) => m.uuid === task.reviewer?.uuid
  );

  const onReviewerChange = async (value: string | null) => {
    try {
      await updateTaskAssigneeAdmin({
        taskId: task.uuid,
        reviewerId: value || undefined,
        removeReviewer: !value,
      }).unwrap();
      successToast({ message: "Updated" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  return (
    <ConditionalToolTip
      condition={
        !values.serviceTeamId &&
        "Please select a service team to add a reviewer"
      }
    >
      <div>
        <Combobox
          size="small"
          isDisabled={!values.serviceTeamId}
          isLoading={serviceTeamLoading || isUpdating}
          label="Reviewer"
          options={serviceTeam.map(({ name, uuid }) => ({
            label: name,
            value: uuid,
            data: name,
          }))}
          onChange={(option) => {
            if (!(option instanceof Array)) {
              onReviewerChange(option?.value || null);
            }
          }}
          value={
            selectedReviewer
              ? {
                  label: selectedReviewer.name,
                  value: selectedReviewer.uuid,
                }
              : null
          }
        />
      </div>
    </ConditionalToolTip>
  );
};

const ServiceTeamMemberSelector = () => {
  const { values } = useFormikContext<FormData>();
  const { isCustomer } = useRoleBasedView();
  const serviceTeamIds = values.serviceTeamId
    ?.split(",")
    .filter((id) => validate(id))
    .join(",");
  const { data: serviceTeam = [], isLoading: serviceTeamLoading } =
    useGetServiceTeamQuery(
      {
        serviceTeamId: serviceTeamIds!,
      },
      { skip: !serviceTeamIds || isCustomer }
    );
  const selectedServiceTeamMember = serviceTeam.find(
    (m) => m.uuid === values.preparerId
  );

  return (
    <ConditionalToolTip
      condition={
        !values.serviceTeamId &&
        "Please select a service team to add a preparer"
      }
    >
      <div>
        <Combobox
          size="small"
          withForm
          isDisabled={!values.serviceTeamId}
          isLoading={serviceTeamLoading}
          name="preparerId"
          label="Preparer"
          options={serviceTeam.map(({ name, uuid }) => ({
            label: name,
            value: uuid,
            data: name,
          }))}
          value={
            selectedServiceTeamMember
              ? {
                  label: selectedServiceTeamMember.name,
                  value: selectedServiceTeamMember.uuid,
                }
              : null
          }
        />
      </div>
    </ConditionalToolTip>
  );
};

export const TaskSliderContent = ({
  taskId,
  defaultTab,
  isOpen,
  onClose,
  showActionDetailsOnly,
}: {
  taskId: string;
  defaultTab: "DETAILS" | "CHAT";
  isOpen: boolean;
  onClose: () => void;
  showActionDetailsOnly?: boolean;
}) => {
  const previewModal = useModal();
  const [tab, setTab] = useState(defaultTab);
  const { isCpa, isAdmin, isCustomer, isServiceSuperAdmin } =
    useRoleBasedView();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [tempServiceTeamId, setTempServiceTeamId] = useState<null | string>(
    null
  );
  const detailsSliderOpen = useAppSelector(
    (store) => store.channelInfoSlider.showActionCentreSlier
  );

  const etdUpdateReasonModal = useModal();
  const { data: serviceTeams = [] } = useGetAllInkleCPATeamsQuery(
    {
      accessibleTeams: "CPA_TEAM",
    },
    { skip: !isAdmin }
  );

  const onPreviewOpen = (fileId: string) => {
    previewModal.open();
    setPreviewId(fileId);
  };

  const onPreviewClose = () => {
    previewModal.close();
    setPreviewId(null);
  };

  const { data: task, isLoading } = useGetTaskQuery(
    {
      taskId: taskId!,
    },
    { skip: !taskId }
  );

  const [channelId, setChannelId] = useState<string | null>(null);
  const [updateTaskAssigneeForAdmin, { isLoading: updatingAssigneeByAdmin }] =
    useUpdateTaskAssigneeAdminMutation();
  const [
    updateTaskAssigneeForProvider,
    { isLoading: updatingAssigneeByProvider },
  ] = useUpdateTaskAssigneeProviderMutation();
  const { alertToast, successToast } = useToast();
  const { client } = useChatContext();

  const updatingAssignee =
    updatingAssigneeByAdmin || updatingAssigneeByProvider;

  let updateTaskAssignee = isAdmin
    ? updateTaskAssigneeForAdmin
    : updateTaskAssigneeForProvider;

  const channel = useMemo(() => {
    return client.channel("messaging", channelId);
  }, [channelId]);

  const channelUrl =
    typeof task?.channel === "string"
      ? task?.channel
      : task?.channel?.channel_url;

  useEffect(() => {
    if (channelUrl) {
      setChannelId(channelUrl);
    }
  }, [channelUrl]);

  const isTaskInFinalState =
    typeof task?.state === "object" ? task?.state?.is_final : false;

  const onFormChange = (values: FormData) => {
    if (task) {
      let payload = {
        taskId: task?.uuid,
        preparerId: values.preparerId,
        removePreparer: !values.preparerId,
      };

      updateTaskAssignee(payload);
    }
  };

  const updateETD = async (date: Date) => {
    try {
      const estimatedDeliveryTime = dayjs(date).format(YYYY_MM_DD);
      if (task?.uuid) {
        await updateTaskAssignee({
          estimatedDeliveryTime,
          taskId: task?.uuid,
        }).unwrap();
        successToast({ message: "ETD set successfully" });
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleEtdChange = (date: Date) => {
    if (task?.estimated_time_of_delivery?.date) {
      etdUpdateReasonModal.open();
    } else {
      updateETD(date);
    }
  };

  const entityLink = isCpa
    ? `/crm/${task?.group_uuid}/entity/${task?.entity?.uuid}`
    : `/entity/${task?.entity.uuid}`;

  const onServiceTeamChange = (newServiceTeamId: string | null) => {
    if (!newServiceTeamId && task) {
      return updateTaskAssignee({
        taskId: task?.uuid,
        serviceTeamId: undefined,
        preparerId: task.assigned_to?.uuid,
        removePreparer: false,
        removeServiceTeam: true,
      });
    }
    setTempServiceTeamId(newServiceTeamId);
  };

  const serviceTeamChangeConfirm = () => {
    if (tempServiceTeamId && task) {
      updateTaskAssignee({
        taskId: task?.uuid,
        serviceTeamId: tempServiceTeamId,
        preparerId: task.assigned_to?.uuid,
        removePreparer: false,
        removeServiceTeam: false,
      });

      setTempServiceTeamId(null);
    }
  };

  if (detailsSliderOpen) {
    return (
      <Slider.Root open={isOpen} onOpenChange={onClose}>
        <Slider.Content>
          {!channelId ? (
            <div className="t-h-full t-w-full t-flex t-items-center t-justify-center">
              <Loader size="small" />
            </div>
          ) : (
            <div className="t-h-full t-w-full">
              <TaskActionDetails
                channelId={channelId}
                handleSliderClose={showActionDetailsOnly ? onClose : undefined}
              />
            </div>
          )}
        </Slider.Content>
      </Slider.Root>
    );
  }

  return (
    <>
      <Slider.Root open={isOpen} onOpenChange={onClose}>
        <Slider.Content open={isOpen}>
          <Tab.Root
            value={tab}
            asChild
            onValueChange={(v) => setTab(v as typeof tab)}
          >
            <DashboardContainer className="t-h-full">
              <DashboardContainer.Header>
                <Slider.Header
                  bottom={
                    <Tab.List className="!t-pt-0 sm:!t-mb-0 sm:!t-border-b-0">
                      <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
                      <Tab.Trigger value="CHAT">Chat</Tab.Trigger>
                      {isCpa && (
                        <Tab.Trigger value="ACTIVITY">Activity</Tab.Trigger>
                      )}
                    </Tab.List>
                  }
                >
                  <Slider.Title>{task?.title}</Slider.Title>
                  <Slider.Close />
                </Slider.Header>
              </DashboardContainer.Header>
              <DashboardContainer.Content className="t-h-full t-top-0">
                <Tab.Content value="DETAILS" asChild>
                  {isLoading || !task ? (
                    <div className="t-flex t-h-full t-w-full t-justify-center t-items-center">
                      <Loader size="small" />
                    </div>
                  ) : (
                    <>
                      <Slider.Body>
                        <Accordion.Root
                          type="multiple"
                          defaultValue={[
                            "ABOUT",
                            "FILING_DETAILS",
                            "ETD_DETAILS",
                            "PAYMENT_DETAILS",
                          ]}
                          className="t-flex t-gap-4 t-flex-col t-mb-16"
                        >
                          <UnstyledChannel channel={channel}>
                            <ActionCentre
                              groupId={task?.group_uuid || ""}
                              alignToCenter={false}
                            />
                          </UnstyledChannel>
                          <Accordion.Item value="ABOUT">
                            <Accordion.Trigger disabled>
                              About
                            </Accordion.Trigger>
                            <Accordion.Content forceMount>
                              <Formik
                                enableReinitialize
                                initialValues={{
                                  serviceTeamId: task.assigned_team?.uuid || "",
                                  preparerId: task.assigned_to?.uuid || "",
                                }}
                                onSubmit={() => {}}
                              >
                                {({ values, dirty }) => {
                                  const selectedServiceTeam = serviceTeams.find(
                                    (t) => t.uuid === values.serviceTeamId
                                  );

                                  return (
                                    <Form className="t-m-0">
                                      <FormikListener
                                        values={{
                                          preparerId: values.preparerId,
                                        }}
                                        callback={() =>
                                          dirty && onFormChange(values)
                                        }
                                      />
                                      <div className="t-grid t-gap-6 t-grid-cols-2">
                                        <div className="t-col-span-2">
                                          <InfoItem label="Filing name">
                                            <Link
                                              to={`/tax/filings/${task?.uuid}`}
                                            >
                                              {task.title}
                                            </Link>
                                          </InfoItem>
                                        </div>

                                        <InfoItem label="Pricing">
                                          {isNaN(
                                            Number(task?.payment_amount)
                                          ) ? (
                                            task?.payment_amount
                                          ) : (
                                            <AmountSuperScript
                                              amount={Number(
                                                task?.payment_amount
                                              )}
                                            />
                                          )}
                                        </InfoItem>

                                        <InfoItem label="Step">
                                          <TaskStatus
                                            state={task.state}
                                            status={
                                              typeof task?.state === "string"
                                                ? task.status
                                                : task.state?.name
                                            }
                                            statusType={
                                              typeof task?.state === "string"
                                                ? task.state_details?.type
                                                : task.state?.type
                                            }
                                          />
                                        </InfoItem>

                                        <InfoItem label="Entity">
                                          <Link
                                            to={entityLink}
                                            className="t-flex t-gap-1 t-items-center"
                                          >
                                            {task.entity.code_alpha_2 && (
                                              <ReactCountryFlag
                                                countryCode={
                                                  task.entity.code_alpha_2
                                                }
                                                svg
                                              />
                                            )}
                                            <span>{task.entity.name}</span>
                                          </Link>
                                        </InfoItem>

                                        <InfoItem label="Formation">
                                          <span>
                                            {task.entity?.formation_type
                                              ?.name || ""}
                                          </span>
                                        </InfoItem>

                                        <InfoItem label="Deadline">
                                          <TaskDeadlineInfo
                                            deadline={task.deadline}
                                            latestDeadlineUpdate={
                                              task.latest_deadline_update
                                            }
                                          />
                                        </InfoItem>
                                        <div />

                                        {isAdmin && (
                                          <Combobox
                                            size="small"
                                            name="serviceTeamId"
                                            label="Service team"
                                            options={serviceTeams.map((t) => ({
                                              label: t.name,
                                              value: t.uuid,
                                            }))}
                                            withForm
                                            isLoading={updatingAssignee}
                                            onChange={(value) => {
                                              if (!(value instanceof Array)) {
                                                onServiceTeamChange(
                                                  value?.value || null
                                                );
                                              }
                                            }}
                                            value={
                                              selectedServiceTeam
                                                ? {
                                                    label:
                                                      selectedServiceTeam.name,
                                                    value:
                                                      selectedServiceTeam.uuid,
                                                  }
                                                : null
                                            }
                                          />
                                        )}

                                        {(isServiceSuperAdmin || isAdmin) && (
                                          <ServiceTeamMemberSelector />
                                        )}

                                        <div className="t-col-span-2">
                                          {isAdmin && (
                                            <ReviewerSelector task={task} />
                                          )}
                                        </div>

                                        {isCpa && task.assigned_at && (
                                          <InfoItem label="Preparer assigned on">
                                            {formatDate(task.assigned_at)}
                                          </InfoItem>
                                        )}

                                        {isCpa && task.reviewer_assigned_on && (
                                          <InfoItem label="Reviewer assigned on">
                                            {formatDate(
                                              task.reviewer_assigned_on
                                            )}
                                          </InfoItem>
                                        )}
                                        {isAdmin &&
                                          task.task_completed_documents
                                            ?.length > 0 && (
                                            <div className="t-col-span-2">
                                              <InfoItem label="Filed copy">
                                                <div className="t-flex t-flex-col t-gap-1">
                                                  {task.task_completed_documents.map(
                                                    (doc) => (
                                                      <Button
                                                        key={doc.uuid}
                                                        customType="link"
                                                        onClick={() =>
                                                          onPreviewOpen(
                                                            doc.uuid
                                                          )
                                                        }
                                                      >
                                                        <span className="t-whitespace-break-spaces t-text-left">
                                                          {doc.name}
                                                        </span>
                                                      </Button>
                                                    )
                                                  )}
                                                </div>
                                              </InfoItem>
                                            </div>
                                          )}
                                      </div>
                                    </Form>
                                  );
                                }}
                              </Formik>
                            </Accordion.Content>
                          </Accordion.Item>

                          {(task.description ||
                            (task.doc_data?.data &&
                              task.doc_data?.data.length > 0) ||
                            task.eligibility ||
                            task.penalty_fee) && (
                            <Accordion.Item value="FILING_DETAILS">
                              <Accordion.Trigger>
                                Filing details
                              </Accordion.Trigger>
                              <Accordion.Content>
                                <Accordion.ItemGrid>
                                  {task.description && (
                                    <div className="t-col-span-2 [&_ul]:t-m-0">
                                      <InfoItem label="Description">
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: task.description || "",
                                          }}
                                        />
                                      </InfoItem>
                                    </div>
                                  )}

                                  {task.doc_data?.data &&
                                    task.doc_data?.data.length > 0 && (
                                      <div className="t-col-span-2">
                                        <InfoItem label="Documents required">
                                          <ul className="t-m-0">
                                            {task.doc_data?.data
                                              ?.map((d) => d.documents_needed)
                                              .flat()
                                              .map((d) => (
                                                <li key={d.field_key}>
                                                  {d.label || d.field_label}
                                                </li>
                                              ))}
                                          </ul>
                                        </InfoItem>
                                      </div>
                                    )}

                                  {task.eligibility && (
                                    <div className="t-col-span-2">
                                      <InfoItem label="Eligibility">
                                        {task.eligibility}
                                      </InfoItem>
                                    </div>
                                  )}

                                  {task.penalty_fee && (
                                    <div className="t-col-span-2">
                                      <InfoItem label="Penalty">
                                        {isNaN(Number(task.penalty_fee)) ? (
                                          task.penalty_fee
                                        ) : (
                                          <AmountSuperScript
                                            amount={Number(task.penalty_fee)}
                                          />
                                        )}
                                      </InfoItem>
                                    </div>
                                  )}
                                </Accordion.ItemGrid>
                              </Accordion.Content>
                            </Accordion.Item>
                          )}

                          {isCpa && (
                            <Accordion.Item value="ETD_DETAILS">
                              <Accordion.Trigger>
                                Estimated time of delivery
                              </Accordion.Trigger>
                              <Accordion.Content>
                                <Formik
                                  enableReinitialize
                                  initialValues={{
                                    estimatedDeliveryTime:
                                      task.estimated_time_of_delivery?.date ||
                                      "",
                                  }}
                                  onSubmit={() => {}}
                                >
                                  {({ values, setFieldValue }) => {
                                    return (
                                      <Form className="t-m-0">
                                        <ConditionalToolTip
                                          condition={
                                            (!task.assigned_team?.uuid &&
                                              "Please select a service team to add a ETD") ||
                                            (isTaskInFinalState &&
                                              "This filing is completed and delivered.")
                                          }
                                        >
                                          <div>
                                            <Field name="estimatedDeliveryTime">
                                              {({ field }: FieldProps) => {
                                                return (
                                                  <>
                                                    <DateInput
                                                      customSize="small"
                                                      disabled={
                                                        !task.assigned_team
                                                          ?.uuid ||
                                                        isTaskInFinalState
                                                      }
                                                      {...field}
                                                      saveFormat={YYYY_MM_DD}
                                                      portalId="add-manual-bank-date"
                                                      label="Estimated time of delivery"
                                                      block
                                                      onDateChange={(date) => {
                                                        setFieldValue(
                                                          "estimatedDeliveryTime",
                                                          date
                                                        );
                                                        handleEtdChange(date);
                                                      }}
                                                    />
                                                  </>
                                                );
                                              }}
                                            </Field>
                                          </div>
                                        </ConditionalToolTip>
                                        {task.etd_history?.length > 0 && (
                                          <div className="t-mt-6">
                                            <InfoItem label="History">
                                              <div className="t-flex t-flex-col t-w-full t-space-y-2 t-ml-2 t-mt-1">
                                                {task.etd_history?.map(
                                                  (
                                                    {
                                                      profile_name,
                                                      event,
                                                      event_time,
                                                      event_description,
                                                      uuid,
                                                    },
                                                    index
                                                  ) => (
                                                    <EtdUpdateHistoryCard
                                                      key={uuid}
                                                      isLastStep={
                                                        index ===
                                                        task.etd_history
                                                          .length -
                                                          1
                                                      }
                                                      description={
                                                        event_description
                                                      }
                                                      eventDate={event_time}
                                                      profileName={profile_name}
                                                      event={event}
                                                    />
                                                  )
                                                )}
                                              </div>
                                            </InfoItem>
                                          </div>
                                        )}
                                        {task?.uuid &&
                                          etdUpdateReasonModal.isOpen && (
                                            <ETDUpdateReason
                                              isOpen={
                                                etdUpdateReasonModal.isOpen
                                              }
                                              close={etdUpdateReasonModal.close}
                                              taskId={task.uuid}
                                              selectedDate={
                                                values.estimatedDeliveryTime
                                              }
                                              task={task}
                                            />
                                          )}
                                      </Form>
                                    );
                                  }}
                                </Formik>
                              </Accordion.Content>
                            </Accordion.Item>
                          )}

                          <Accordion.Item value="PAYMENT_DETAILS">
                            <Accordion.Trigger>
                              Payment details
                            </Accordion.Trigger>
                            <Accordion.Content>
                              <Accordion.ItemGrid>
                                <InfoItem label="Government fees">
                                  <AmountSuperScript
                                    amount={Number(task?.government_fee)}
                                  />
                                </InfoItem>

                                {task.invoices && task.invoices.length > 0 && (
                                  <InfoItem label="Invoice">
                                    <div className="t-flex t-flex-wrap t-gap-1">
                                      {task.invoices?.map((i) => (
                                        <Button
                                          key={i.uuid}
                                          customType="link"
                                          onClick={() => onPreviewOpen(i.uuid)}
                                        >
                                          <span className="t-whitespace-break-spaces t-text-left">
                                            #invoice{i.name}
                                          </span>
                                        </Button>
                                      ))}
                                    </div>
                                    <Preview
                                      showModal={previewModal.isOpen}
                                      closeModal={onPreviewClose}
                                      groupId={task.group_uuid}
                                      previewId={previewId}
                                    />
                                  </InfoItem>
                                )}
                              </Accordion.ItemGrid>
                            </Accordion.Content>
                          </Accordion.Item>
                        </Accordion.Root>
                      </Slider.Body>
                      <Slider.Footer className="!t-sticky">
                        <ConditionalLink to={`/tax/filings/${task?.uuid}`}>
                          <Button
                            customType="primary"
                            size="small"
                            block
                            disabled={isLoading || !task}
                          >
                            Go to Filing
                          </Button>
                        </ConditionalLink>
                      </Slider.Footer>
                    </>
                  )}
                </Tab.Content>

                <Tab.Content value="CHAT" asChild>
                  {isLoading || !task ? (
                    <div className="t-flex t-h-full t-w-full t-justify-center t-items-center">
                      <Loader size="small" />
                    </div>
                  ) : (
                    <ChatPane
                      channelId={channelId}
                      setChannelId={setChannelId}
                    />
                  )}
                </Tab.Content>
                <Tab.Content value="ACTIVITY" asChild>
                  <div className="t-pt-5 t-h-full">
                    {task && (
                      <AuditlogList
                        entityId={task?.entity.uuid}
                        objectId={task?.uuid}
                      />
                    )}
                  </div>
                </Tab.Content>
                {tempServiceTeamId && (
                  <ServiceTeamChangeModal
                    isOpen={Boolean(tempServiceTeamId)}
                    onClose={() => setTempServiceTeamId(null)}
                    serviceTeamId={tempServiceTeamId}
                    onSubmit={serviceTeamChangeConfirm}
                  />
                )}
              </DashboardContainer.Content>
            </DashboardContainer>
          </Tab.Root>
        </Slider.Content>
      </Slider.Root>
    </>
  );
};

export const TaskSlider = ({
  taskId,
  isOpen,
  onClose,
  defaultTab = TASK_SLIDER_TABS.DETAILS,
  showActionDetailsOnly,
}: {
  taskId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: TaskSliderTabs;
  showActionDetailsOnly?: boolean;
}) => {
  if (!taskId) {
    return (
      <Slider.Root>
        <Slider.Content></Slider.Content>
      </Slider.Root>
    );
  }

  return (
    <TaskSliderContent
      defaultTab={defaultTab}
      taskId={taskId || ""}
      isOpen={isOpen}
      onClose={onClose}
      showActionDetailsOnly={showActionDetailsOnly}
    />
  );
};
